import { ValidationInterface } from '../../validation.interface';
import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import { UnitValueValidation } from '../unit-value.validation';
import { Loan, LoanPropName, LoanType } from '../../../../models/project/financial/financial-investment/loan.model';

export class LoanValidation implements ValidationInterface {
  /** The current loan obj that is being validated */
  private readonly loan: Loan;

  /**
   * Sets the loan obj that will be used for validation
   * @param loan will be used to set the current loan obj
   */
  constructor(loan: Loan) {
    if (!loan) throw Error('invalid loan provided for validation');
    this.loan = new Loan(loan);
  }

  /**
   * Get a validation unit containing the validation data for the current value obj
   */
  getValidation(): ProjectValidationUnit {
    if (!this.loan) throw Error('unable to validate loan');
    const validationUnit = new ProjectValidationUnit();
    if (this.loan.name) {
      validationUnit.setValid(LoanPropName.name);
    } else {
      validationUnit.setInvalid(LoanPropName.name, [LoanError.MissingName]);
    }

    if (Object.values(LoanType).includes(this.loan.loanType as LoanType)) {
      validationUnit.setValid(LoanPropName.loanType);
    } else {
      validationUnit.setInvalid(LoanPropName.loanType, [LoanError.MissingType]);
    }

    if (this.loan.loanAmount) {
      validationUnit.addModuleResults(
        new UnitValueValidation(this.loan.loanAmount).getValidation(),
        LoanPropName.loanAmount
      );
    }

    if (this.loan.loanType !== LoanType.Forgiven) {
      if (this.loan.interestRate) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.loan.interestRate).getValidation(),
          LoanPropName.interestRate
        );
      }

      if (this.loan.amortizationPeriod) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.loan.amortizationPeriod).getValidation(),
          LoanPropName.amortizationPeriod
        );
      }
    }

    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current loan obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }
}

/**
 * An enum that contains error that relate to loan errors
 */
export enum LoanError {
  MissingName = 'VALIDATION.MISSING_NAME',
  MissingType = 'VALIDATION.MISSING_TYPE',
}
